import { useState } from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Text } from "@ps-design-system/components/Text";

import type { AmenityOption } from "~/services/env.service";

import type { ListingUseType } from "../../../../components/src/services/listings.service";
import { Space } from "../../../../types/spaces.types";

import { ActivateListingCard } from "./ActivateListingCard";
import type { ListingUseTypeConfig } from "./ActivateListingsPage.types";
import { useActivateListingsForms } from "./useActivateListingsForms";

type ActivateListingsContentProps = {
  space: Space;
  inactiveUseTypes: ListingUseTypeConfig[];
  amenitiesByUseType: Record<ListingUseType, AmenityOption[]>;
  isInstantBookEligible: boolean;
};

const ActivateListingsPageContent = ({
  space,
  inactiveUseTypes,
  amenitiesByUseType,
  isInstantBookEligible,
}: ActivateListingsContentProps) => {
  const [selectedUseTypes, setSelectedUseTypes] = useState<ListingUseType[]>(
    [],
  );

  const { formsByUseType, submitAllForms, isSubmitting, errorMsg } =
    useActivateListingsForms(amenitiesByUseType, isInstantBookEligible);

  const onActivateListings = async () => {
    const success = await submitAllForms(
      space._id,
      selectedUseTypes,
      space!.country || "US",
    );
    if (success) {
      window.location.href =
        space.status === "DRAFT"
          ? `/your-space/${space._id}/policies`
          : "/mylistings";
    }
  };

  const onEnableClick = (open: boolean, useType: ListingUseType) => {
    setSelectedUseTypes((prev) => {
      return open
        ? [...prev, useType]
        : prev.filter((useType) => useType !== useType);
    });
  };

  if (inactiveUseTypes.length === 0) {
    return (
      <Text
        typeface="body-2"
        element="p"
        className="tw-p-16 tw-bg-gray-100 tw-rounded-lg tw-mt-16"
        testId="activate-listings-all-activated-message"
      >
        <FormattedMessage
          id="hosting.activate_listings.all_activated"
          defaultMessage="You already created listings for all uses, {link} to edit or delete them."
          values={{
            link: (
              <a
                href="/mylistings"
                className="tw-underline"
                data-testid="activate-listings-all-activated-see-listings-link"
              >
                <FormattedMessage
                  id="hosting.activate_listings.see_your_listings"
                  defaultMessage="see your listings"
                />
              </a>
            ),
          }}
        />
      </Text>
    );
  }

  return (
    <div>
      <div
        className="tw-grid tw-gap-16 tw-mt-32 tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3"
        data-testid="activate-listings-cards"
      >
        {inactiveUseTypes.map((config) => (
          <div key={config.useType} className="tw-col-span-1">
            <ActivateListingCard
              config={config}
              form={formsByUseType[config.useType]}
              spaceCountry={space?.country ?? "US"}
              amenityOptions={amenitiesByUseType[config.useType]}
              isInstantBookEligible={isInstantBookEligible}
              onOpenChange={(open: boolean) =>
                onEnableClick(open, config.useType)
              }
            />
          </div>
        ))}
      </div>

      <Divider />

      <div className="tw-flex tw-justify-between tw-mt-32 tw-items-start">
        <Button
          appearance="white"
          variant="outline-1"
          href="/mylistings"
          testId="activate-listings-back-button"
          className="hover:tw-text-gray-900"
        >
          <FormattedMessage
            id="hosting.activate_listings.cancel_button"
            defaultMessage="Back"
          />
        </Button>

        <div className="tw-flex tw-flex-col tw-items-end tw-gap-8">
          <Button
            appearance="purple"
            disabled={selectedUseTypes.length === 0}
            onClick={onActivateListings}
            showSpinner={isSubmitting}
            testId="activate-listings-submit-button"
          >
            <FormattedMessage
              id="hosting.activate_listings.create_listings_button"
              defaultMessage="Create listings"
            />
          </Button>
          {errorMsg && (
            <Text
              typeface="body-1"
              element="p"
              className="tw-text-red-800"
              testId="activate-listings-submit-error"
            >
              {errorMsg}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export { ActivateListingsPageContent, type ActivateListingsContentProps };
